import { normalizeApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Pcs } from '../api/endpoints/pcsApi';
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { SummaryDto } from "../models/summary/summaryDto";
import { PCSDetailsDto } from "../models/pcsEnrolment/pcsDetailsDto";
import { UpdateEducationCommand } from "../models/person/updateEducationCommand";
import { PCSSummaryDto } from "../models/pcs/pcsSummaryDto";

interface PcsState {
    currentYear: number | null;
    summaryTitles: string[] | null;
    summaryData: SummaryDto[][] | null;
    legacyTitles: string[] | null;
    legacySummaryData: SummaryDto[][] | null;
    pcsSummaryDto: PCSSummaryDto | null;
    registrationDetails: PCSDetailsDto | null;
    canEnrol: boolean | null;
    canReEnrol: boolean | null;

    registerStateStatus: ApiRequestStatus;
    saveRegistrationDetailsStatus: ApiRequestStatus;
    currentYearStatus: ApiRequestStatus;
    getCreditSummaryStatus: ApiRequestStatus;
    updateEducationalDetailsStatus: ApiRequestStatus;
    canEnrolStatus: ApiRequestStatus;
    canReEnrolStatus: ApiRequestStatus;
}

const initialState: PcsState = {
    currentYear: null,
    summaryTitles: null,
    summaryData: null,
    legacyTitles: null,
    legacySummaryData: null,
    pcsSummaryDto: null,
    registrationDetails: null,
    canEnrol: null,
    canReEnrol: null,
    registerStateStatus: ApiRequestStatus.Idle,
    saveRegistrationDetailsStatus: ApiRequestStatus.Idle,
    currentYearStatus: ApiRequestStatus.Idle,
    getCreditSummaryStatus: ApiRequestStatus.Idle,
    updateEducationalDetailsStatus: ApiRequestStatus.Idle,
    canEnrolStatus: ApiRequestStatus.Idle,
    canReEnrolStatus: ApiRequestStatus.Idle
}

export const getPcsCurrentYearAsync = createReadAsyncThunk<number>(
    'pcsSlice/getPcsCurrentYearAsync',
    async (_, thunkAPI) => {
        try {
            return await Pcs.getCurrentYearAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getRegistrationDetailsAsync = createReadAsyncThunk<PCSDetailsDto>(
    'pcsSlice/getRegistrationDetailsAsync',
    async (_, thunkAPI) => {
        try {
            return await Pcs.getRegistrationDetailsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getCanEnrolAsync = createReadAsyncThunk<boolean>(
    'pcsSlice/getCanEnrolAsync',
    async (_, thunkAPI) => {
        try {
            return await Pcs.getCanEnrolAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getCanReEnrolAsync = createReadAsyncThunk<boolean>(
    'pcsSlice/getCanReEnrolAsync',
    async (_, thunkAPI) => {
        try {
            return await Pcs.getCanReEnrolAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getCreditSummaryAsync = createReadAsyncThunk<PCSSummaryDto>(
    'pcsSlice/getCreditSummaryAsync',
    async (_, thunkAPI) => {
        try {
            return await Pcs.getCreditSummaryAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const saveRegistrationDetailsAsync = createAsyncThunk<void, PCSDetailsDto>(
    'pcsSlice/saveRegistrationDetailsAsync',
    async (registrationDetails, thunkAPI) => {
        try {
            return await Pcs.saveRegistrationDetailsAsync(registrationDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const updateEducationAsync = createAsyncThunk<void, UpdateEducationCommand>(
    'pcsSlice/updateEducationAsync',
    async (educationalDetails, thunkAPI) => {
        try {
            return await Pcs.updateEducationAsync(educationalDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const pcsSlice = createSlice({
    name: 'pcsSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(getPcsCurrentYearAsync.pending, (state) => {
            state.currentYearStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsCurrentYearAsync.fulfilled, (state, action) => {
            state.currentYear = action.payload;
            state.currentYearStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsCurrentYearAsync.rejected, (state) => {
            state.currentYearStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getRegistrationDetailsAsync.pending, (state) => {
            state.registerStateStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getRegistrationDetailsAsync.fulfilled, (state, action) => {
            state.registerStateStatus = ApiRequestStatus.Fulfilled;
            state.registrationDetails = action.payload;
        });
        builder.addCase(getRegistrationDetailsAsync.rejected, (state) => {
            state.registerStateStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getCanEnrolAsync.pending, (state) => {
            state.canEnrolStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCanEnrolAsync.fulfilled, (state, action) => {
            state.canEnrolStatus = ApiRequestStatus.Fulfilled;
            state.canEnrol = action.payload;
        });
        builder.addCase(getCanEnrolAsync.rejected, (state) => {
            state.canEnrolStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getCanReEnrolAsync.pending, (state) => {
            state.canReEnrolStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCanReEnrolAsync.fulfilled, (state, action) => {
            state.canReEnrolStatus = ApiRequestStatus.Fulfilled;
            state.canReEnrol = action.payload;
        });
        builder.addCase(getCanReEnrolAsync.rejected, (state) => {
            state.canReEnrolStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getCreditSummaryAsync.pending, (state) => {
            state.getCreditSummaryStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCreditSummaryAsync.fulfilled, (state, action) => {
            state.getCreditSummaryStatus = ApiRequestStatus.Fulfilled;
            state.summaryTitles = action.payload.titles;
            state.summaryData = action.payload.summaryData;
            state.legacyTitles = action.payload.legacyTitles;
            state.legacySummaryData = action.payload.legacySummaryData;
            state.pcsSummaryDto = action.payload;
        });
        builder.addCase(getCreditSummaryAsync.rejected, (state) => {
            state.getCreditSummaryStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(saveRegistrationDetailsAsync.pending, (state) => {
            state.saveRegistrationDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(saveRegistrationDetailsAsync.fulfilled, (state, action) => {
            state.registrationDetails = { ...state.registrationDetails, ...action.meta.arg };
            state.saveRegistrationDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(saveRegistrationDetailsAsync.rejected, (state) => {
            state.saveRegistrationDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(updateEducationAsync.pending, (state) => {
            state.updateEducationalDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(updateEducationAsync.fulfilled, (state, action) => {
            if (state.registrationDetails) {
                state.registrationDetails.qualificationCountryId = action.meta.arg.qualificationCountryId;
                state.registrationDetails.qualificationInstitutionId = action.meta.arg.qualificationInstitutionId;
                state.registrationDetails.qualificationInstitutionOther = action.meta.arg.qualificationInstitutionOther;
                state.registrationDetails.qualificationYear = action.meta.arg.qualificationYear;
            }
            state.updateEducationalDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(updateEducationAsync.rejected, (state) => {
            state.updateEducationalDetailsStatus = ApiRequestStatus.Rejected;
        });
    })
})
